<template>
    <v-app>
        <v-app-bar app color="#231f20" dark>
            <v-layout justify-space-between fill-height align-center>
                <v-flex sm6 md3 class="logo-and-lang">
                    <LogoLarge />
                    <LanguageDropdown class="ml-8" />
                </v-flex>
                <v-flex md6 v-if="!smAndDown">
                    <v-layout justify-center align-center>
                        <!-- <div> -->
                        <form v-if="log.length === 0 && !isObserver" @submit.prevent="requestLocation">
                            <v-layout>
                                <v-text-field
                                    data-v-step="1"
                                    v-model="phoneNumber"
                                    name="phoneNumber"
                                    flat
                                    solo
                                    mask="phone"
                                    :label="t('layouts.app.phoneNumber')"
                                    light
                                    style="border-top-right-radius:0;border-bottom-right-radius:0"
                                    autofocus
                                    hide-details
                                    data-test="phone-number-input"
                                />
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on }">
                                        <v-btn
                                            type="submit"
                                            outlined
                                            dark
                                            style="margin:0 0 0 -2px;height:48px;"
                                            data-test="start-locating-button"
                                            v-on="on"
                                            >{{ t('layouts.app.locate') }}</v-btn
                                        >
                                    </template>
                                    <span>{{ t('layouts.app.locateTooltip') }}</span>
                                </v-tooltip>
                            </v-layout>
                            <v-snackbar
                                v-model="showErrorSnackbar"
                                :timeout="3000"
                                top
                                left
                                data-test="valid-phone-number-error-snackbar"
                                >{{ t('layouts.app.validPhoneError') }}</v-snackbar
                            >
                        </form>
                        <template v-else>
                            <div v-if="!isObserver">
                                <span style="margin-right: 10px">{{ t('layouts.app.locating', { phoneNumber }) }}</span>
                                <span class="end-tracking-container">
                                <EndTrackingModal>
                                    <v-btn outlined dark data-test="end-tracking-button">{{ t('layouts.app.endTracking') }}</v-btn>
                                </EndTrackingModal>
                                <v-icon
                                    v-if="showObserving"
                                    class="observing-eye"
                                    small
                                    color="#f44336"
                                >$vuetify.icons.eye_outline</v-icon>
                                </span>
                            </div>
                        </template>
                        <template v-if="isObserver">
                            <span class="end-observation-container">
                                <EndObservationModal>
                                <v-btn class="observe" data-test="end-observation-button">{{ t('layouts.app.endObservation') }}</v-btn>
                                </EndObservationModal>
                            </span>
                        </template>
                    </v-layout>
                    <!-- </div> -->
                </v-flex>
                <v-flex style="display: flex; align-items: center; justify-content: flex-end;" sm6 md3>
                    <template v-if="customHeaderLinks">
                        <v-btn
                            text
                            dark
                            v-for="(link, index) in customHeaderLinks"
                            :key="index"
                            :href="link.link"
                            target="_blank"
                        >{{ link.name }}</v-btn>
                    </template>
                    <UserMenu :user="user" :subtitle="user.companyName || teamName">
                        <UserMenuItem icon="addressCard" @click="toProfile">
                            {{ t('layouts.app.profile') }}
                        </UserMenuItem>
                        <UserMenuItem v-if="isUserAdmin" icon="users" :to="{ name: 'admin' }">
                            {{ t('layouts.app.admin') }}
                        </UserMenuItem>
                        <UserMenuItem v-if="premiere" icon="eye" :to="{ name: 'overwatch' }">
                            {{ t('layouts.app.overwatch') }}
                        </UserMenuItem>
                        <UserMenuItem v-if="premiere" icon="viewer" :to="{ name: 'liveteam' }">
                            {{ t('layouts.app.teamTrack') }}
                        </UserMenuItem>
                        <UserMenuItem icon="file" @click="toTraining">
                            {{ t('layouts.app.trainingVideos') }}
                        </UserMenuItem>
                        <UserMenuItem icon="edit" @click="changeEmail">
                            {{ t('layouts.training.changeEmail') }}
                        </UserMenuItem>
                        <UserMenuItem icon="globe" href="https://i911help.zendesk.com/hc/en-us/sections/360009792793-FAQ" target="_blank">
                            {{ t('layouts.app.faq') }}
                        </UserMenuItem>
                        <UserMenuItem
                            v-for="(link, index) in customHeaderDropdownLinks"
                            :key="index"
                            icon="link"
                            :href="link.link"
                            target="_blank"
                        >
                            {{ link.name }}
                        </UserMenuItem>
                        <UserMenuItem icon="sign_out" danger @click="signOut">
                            {{ t('layouts.app.signOut') }}
                        </UserMenuItem>
                    </UserMenu>
                </v-flex>
            </v-layout>
        </v-app-bar>
        <v-main>
            <v-alert v-if="seen" class="alert" :style="{ position: 'fixed', top: 50, zIndex:1 }"><p class="sms-text">{{ t('layouts.app.smsReceived') }}</p></v-alert>
            <slot />
        </v-main>
        <!-- <DashboardTour /> -->
    </v-app>
</template>

<script lang="ts">
import Vue from 'vue'
import store from '@/store'
import {eventBus} from "@/main";
import axios from 'axios';

// Must be high in the componnet heirarchy for correct DOM positioning.
import DashboardTour from '@/components/DashboardTour.vue'
import EndTrackingModal from '@/components/EndTrackingModal.vue'
import EndObservationModal from '@/components/EndObservationModal.vue';
import LogoLarge from '@/components/LogoLarge.vue';

export default Vue.extend({
    data() {
        return {
            showErrorSnackbar: false,
            teamName: null,
            seen: false,
            premiere: false
        }
    },
    created() {
        this.teamName = store.state.authentication.currentUser.companyName
        eventBus.$on('triggerSmsBanner', () => {
            this.triggerSmsBanner()
        })

        const level = "premiere"
        axios.get(`/api/v1/teams/checkPermissionLevel/${this.teamName}/${level}`)
        .then(res => {
            this.premiere = res.data //returns either a 1 or 0 (T/F)
        })
        // Connects presence socket on creation
        if (!store.state.location.hasActiveSearch) {
            store.dispatch('location/connectPresence')
        }
    },
    methods: {
        t(key: string, params?: any): string {
            return (this as any).$t(key, params)
        },
        async requestLocation() {
            const isValid = await this.$validator.validateAll()
            if (isValid) {
                store.dispatch('location/requestLocation')
                // this.$tours.dashboardTour.stop()
            } else {
                this.showErrorSnackbar = true
            }
        },
        toProfile() {
            store.commit('location/showUserProfileModal', true)
        },
        changeEmail() {
            store.commit('location/showEditEmailModal', true)
        },
        signOut() {
            store.dispatch('location/disconnectPresence')
            store.dispatch('authentication/logout')
            store.dispatch('location/reset')
            // this.$tours.dashboardTour.stop()
            this.$router.push({
                name: 'auth-sign-in'
            })
        },
        triggerSmsBanner() {
            this.seen = true;
            setTimeout(() => {
                this.seen = false;
            }, 3000);
        },
        toTraining() {
            this.$router.push({
                name: 'training'
            })
        },
        beforeDestroy() {
            store.dispatch('location/disconnectPresence')
        },
    },
    computed: {
        phoneNumber: {
            get(): string {
                return store.state.location.phoneNumber
            },
            set(value: string) {
                store.commit('location/phoneNumber', value)
            }
        },
        isObserver() {
            return store.state.location.isObserver
        },
        showObserving() {
            return !!store.state.location.observerConnected && !store.state.location.isObserver
        },
        log(): any {
            return store.state.location.log
        },
        smAndDown(): any {
            return this.$vuetify.breakpoint.smAndDown
        },
        user(): any {
            return store.state.authentication.currentUser
        },
        customHeaderDropdownLinks(): any {
            const teams = store.state.authentication.teams
            if (!teams || teams.length == 0) return []

            const team = teams[0]
            const links = team.customContent && team.customContent.links && team.customContent.links.headerDropdown
            if (links) return links
            return []
        },
        customHeaderLinks(): any {
            const teams = store.state.authentication.teams
            if (!teams || teams.length == 0) return []

            const team = teams[0]
            const links = team.customContent && team.customContent.links && team.customContent.links.header
            if (links) return links
            return []
        },
        isUserAdmin() {
            const user = store.state.authentication.currentUser
            return user && (user.isAdmin || user.isTeamAdmin || user.isAgencyAdmin)
        },
        a2pModal() {
            return store.state.location.showA2PMessagingModal
        }
    },
    components: {
        // DashboardTour,
        EndTrackingModal,
        EndObservationModal,
    }
})
</script>
<style lang="scss">
.v-step {
    z-index: 100;
}

// .location-number-container {
//     @media (max-width: 1244px) {
//         position: relative;
//         float: left;
//         width: 70%;
//         top: 15px;
//         margin: 0;
//     }
// }

// .end-tracking-container {
//     @media (max-width: 1244px) {
//         position: relative;
//         float: right;
//         width: 30%;
//         margin: 0;
//     }
// }

.end-tracking-container {
    display: inline-flex;
    align-items: center;
}

.observing-eye {
    margin-left: 10px;
    animation: observing-eye-pulse 1.4s ease-in-out infinite;
}

@keyframes observing-eye-pulse {
    0%,
    100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.45;
        transform: scale(0.88);
    }
}

.alert {
  width: 100%;
  height: 4% !important;
  background-color:#FF0000 !important;
  border-radius:0 !important;
  padding-top: 7px !important;
}

.sms-text {
  color: white !important;
  font-weight: bold;
  font-size: 19px;
  text-align: center;
  margin-bottom: 20px !important;
}

.observe {
    background-color: #dc2626 !important;
    color: white !important;
}

.logo-and-lang {
  display: flex;
  align-items: center;
}
</style>
